import Grid from '@mui/material/Grid';
import { Box, Paper, Stack, Tab, Tabs, Typography } from '@mui/material';
import { DateRangePicker, DateRange, LocalizationProvider } from '@mui/x-date-pickers-pro';
import dayjs, { Dayjs } from 'dayjs';
import { useMemo, useState } from 'react';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import 'dayjs/locale/de';

import DashboardCard from './DashboardCard';
import { useFetch } from '../hooks/useFetch';
import { useLocation } from 'react-router-dom';
import { IDashboardDataItem, IResponse, Aggregated} from './DashboardModel'
import DashboardCards from './DashboardCards';

type Range = { dateFrom: Dayjs; dateTo: Dayjs };

export const getFiscalRange = (today: Dayjs = dayjs()): Range => {
  const y = today.year();
  const fiscalStartThisYear = dayjs(`${y}-10-01`);

  if (today.isBefore(fiscalStartThisYear, "day")) {
    return {
      dateFrom: dayjs(`${y - 1}-10-01`),
      dateTo:   dayjs(`${y}-09-30`),
    };
  }
  return {
    dateFrom: dayjs(`${y}-10-01`),
    dateTo:   dayjs(`${y + 1}-09-30`),
  };
};

function Dashboard() {
    const { dateFrom: fiscalFrom, dateTo: fiscalTo } = getFiscalRange();
    const [dateRange, setDateRange] = useState<DateRange<Dayjs>>([dayjs().add(-1, 'month'), dayjs().add(1, 'day')]);
    const data = useFetch<IResponse | undefined>(`/api/maincontrol/TuiFlyCamService.svc/GetDashboardData?from=${fiscalFrom.format('YYYY-MM-DD')}&to=${fiscalTo.format('YYYY-MM-DD')}`, (response) => response, 0, true).data;
    const [tabIndex, setTabIndex] = useState(0);
    const location = useLocation();

     const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        let pageState = { tabIndex: newValue };
        localStorage.setItem(`PageState_${location.pathname}`, JSON.stringify(pageState));
        setTabIndex(newValue);
    };

    const aggregateByKey = (items: IDashboardDataItem[]) : Aggregated[] => {
        const map = new Map<string, Aggregated>();

        for (const it of items) {
            const v = Number(it.Value) || 0;
            const existing = map.get(it.Key);
            if (existing) {
            existing.Value += v;
            } else {
            map.set(it.Key, { Key: it.Key, Name: it.Name, Value: v, Subtitle: it.Subtitle });
            }
        }

        return Array.from(map.values());
    }

    const aggregatedFiscalYear = useMemo(() => {
        var list = (data?.Payload ?? []).map(o => { return { ...o, Subtitle: '' }});

        switch(tabIndex) {
            case 1: // TUI Airways
                list = list.filter(o => (o.ClientCode ?? 'BAL').toUpperCase() === 'BAL');
                break;
            case 2: // TUIfly BE
                list = list.filter(o => (o.ClientCode ?? 'JAF').toUpperCase() === 'JAF');
                break;
            case 3: // TUIfly Germany
                list = list.filter(o => (o.ClientCode ?? 'TUI').toUpperCase() === 'TUI');
                break;
            case 4: // TUIfly NL
                list = list.filter(o => (o.ClientCode ?? 'TFL').toUpperCase() === 'TFL');
                break;
            case 5: // TUIfly Nordic
                list = list.filter(o => (o.ClientCode ?? 'BLX').toUpperCase() === 'BLX');
                break;
            default:
                // no filter
        }

        return aggregateByKey(list)
    }, [data, tabIndex])

    return (
        <Grid container spacing={3}>
            <Grid item xs={12}>
                <Paper sx={{ p: 2, paddingBottom: 0, display: 'flex', flexWrap: 'wrap', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'center' }} elevation={3}>
                    <Box sx={{flexGrow: 1, mr: { xs: 0, md: 2 }, mb: { xs: 2, md: 0 } }}>
                        <Typography variant="subtitle1" component="h2" sx={{ textTransform: 'uppercase' }}>
                            Dashboard
                        </Typography>
                        <Stack direction='row' spacing={2} style={{float:'right', marginTop:-20, marginBottom:10}}>
                            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en">
                                <DateRangePicker value={dateRange} onChange={setDateRange} slotProps={{ textField: { size: 'small' } }} />
                            </LocalizationProvider>
                        </Stack>
                    </Box>

                    <div style={{flexBasis:'100%', flexGrow:1}}>
                                            
                    </div>
                    <Tabs value={tabIndex} onChange={handleTabChange}>
                        <Tab label="All" />
                        <Tab label="TUI Airways" />
                        <Tab label="TUIfly BE" />
                        <Tab label="TUIfly Germany" />
                        <Tab label="TUIfly NL" />
                        <Tab label="TUIfly Nordic" />
                    </Tabs>
                </Paper>
            </Grid>
            <Grid item xs={12}>
                <Stack direction='row' spacing={2} flexWrap='wrap' useFlexGap sx={{ marginBottom:2,
                    "& > *": {
                    width: 250,   // Fixed width to keep them sync on wrapping
                    flexShrink: 0 // Prevent shrinking
                    }
                }}>
                    { aggregatedFiscalYear.map(o => (
                        <DashboardCard title={o.Name ?? 'Unkown'} subtitle={o.Subtitle ?? 'Unkown'} value={new Intl.NumberFormat("en-GB", { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(o.Value)} />
                    ))}
                    
                </Stack>

                <DashboardCards dateFrom={dateRange[0]?.format("YYYY-MM-DD")} dateTo={dateRange[1]?.format("YYYY-MM-DD")} tabIndex={tabIndex} />
            </Grid>
        </Grid>
    );
}

export default Dashboard;